import {Component, DestroyRef, Input, OnInit} from '@angular/core';
import {NgClass} from '@angular/common';
import {ControlContainer, FormsModule, NgForm, ReactiveFormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {PlanModel, PlanModelService} from '../../common/service/plan-model.service';
import {LookupService} from '../../common/service/lookup.service';
import {DependantPlanActionLookup} from '../../common/models/dependant-plan-action.model';
import {DependantPlan} from '../../common/models/dependant-plan.model';
import {DependantPlanActionFilterLookup} from '../../common/models/dependant-plan-action-filter.model';
import {FormerTenureValidity} from '../../common/models/former-tenure-validity.model';
import {StrataPlanAction} from '../../common/models/strata-plan-action.model';
import {NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NlrFormFieldHasErrorPipe} from '../../common/pipe/nlr-form-field-has-error.pipe';
import {PermissionsService} from '../../common/service/permissions.service';

@Component({
    selector: 'dependencies',
    imports: [ReactiveFormsModule, FormsModule, NgClass, NlrFormFieldHasErrorPipe, NgSelectModule],
    templateUrl: './dependencies.component.html',
    styleUrl: './dependencies.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class DependenciesComponent implements OnInit {
    @Input()
    title: string;

    @Input()
    dependencyType: string;

    @Input()
    displayConfig: string[];

    // Same value in the dependant plan action lookup for conversion plan and treatment action indicators
    private readonly OPTIONALITY_VALUES = {
        LEGACY: 'Legacy',
        MANDATORY: 'Mandatory',
        OPTIONAL: 'Optional'
    };

    model: PlanModel;

    dependantPlanActionLookups: DependantPlanActionLookup[] = [];

    dependantPlanActionFilterLookups: DependantPlanActionFilterLookup[] = [];

    formerTenureValidityLookups: FormerTenureValidity[] = [];

    matchColumn = undefined;

    invalidFormerTenureText = '';

    editViewAnalysisPermissions = ['EDIT_ANALYSIS', 'VIEW_ANALYSIS'];

    constructor(
        private permissionsService: PermissionsService,
        private planModelService: PlanModelService,
        private lookupService: LookupService,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit() {
        this.model = this.planModelService.getModel();
        this.dependantPlanActionLookups = this.lookupService
            .getLookups('dependantPlanAction')
            .sort((l1: DependantPlanActionLookup, l2: DependantPlanActionLookup) => l1.sortOrder - l2.sortOrder);
        this.dependantPlanActionFilterLookups = this.lookupService.getLookups('dependantPlanActionFilter');
        this.formerTenureValidityLookups = this.lookupService.getLookups('formerTenureValidity');
        this.matchColumn = this.deriveMatchColumnName(this.dependencyType);

        this.eventBus
            .on<string>('planInformation:invalidFormerTenureText')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(event => {
                this.invalidFormerTenureText = event.data;
            });
    }

    actionIds() {
        return this.dependantPlanActionLookups.map(action => action.id);
    }

    actionLookupFromId(id: string) {
        return this.dependantPlanActionLookups.find(dependantPlanActionLookup => dependantPlanActionLookup.id === id);
    }

    /**
     * Maps a user-friendly name dependent plan action name from a DependantPlanActionLookup id.
     *
     * @param id The lookup id.
     * @returns {undefined|*} The user-friendly name for the action, or undefined if the id is undefined (or unknown, but this shouldn't happen).
     */
    actionNameFromId(id: string) {
        return id ? this.actionLookupFromId(id).name : '';
    }

    /**
     * Determines if a dependent plan is applicable to this instance of the Dependencies component.
     *
     * @returns A subset of all the dependent plans on the plan lodgement model that are of this type.
     */
    applicableDependentPlans() {
        if (this.dependencyType === 'STRATA') {
            return this.model.strataPlanActions;
        } else {
            return this.model.dependantPlans.filter(dependentPlan => dependentPlan.dependencyType === this.dependencyType);
        }
    }

    /**
     * Determines whether a table element should be displayed.
     *
     * @param elementName The name of the table element.
     * @returns {boolean} Whether or not to display the element.
     */
    doDisplay(elementName: string) {
        return this.displayConfig?.includes(elementName);
    }

    /**
     * A method to return a filter function that wraps {@link actionApplicableToDependentPlan}.
     *
     * @param dependentPlan
     * @returns {Function} The filter function that removes dependent plan actions that aren't applicable.
     */
    filterAction(dependentPlan: DependantPlan) {
        return (dependentPlanActionId: string) => this.actionApplicableToDependentPlan(dependentPlan, dependentPlanActionId);
    }

    getDependentPlanActionIds(dependentPlan: DependantPlan) {
        return this.actionIds().filter(actionId => this.actionApplicableToDependentPlan(dependentPlan, actionId));
    }

    dependentPlanActionIdTrackBy(actionId: string) {
        return actionId;
    }

    /**
     * Determines if a dependent plan action applies to (is a selectable option for) the given dependent plan.
     *
     * This applies relevant DependantPlanActionFilterLookups to the filtering process.
     * Whether DependantPlanActionFilterLookup is applicable depends on:
     * 1) The dependent plan action
     * 2) Which instance of DependenciesController this is (e.g. Spatial Dependencies)
     * 3) The plan purpose of the current plan lodgement
     * 4) The plan type, tenure type and (sometimes) purpose of the dependent plan.
     *
     * @param dependentPlan         The dependent plan.
     * @param dependentPlanActionId The lookup id of the dependent plan action.
     * @returns {boolean} Whether or not an action applies to the given dependent plan.
     */
    actionApplicableToDependentPlan(dependentPlan: DependantPlan, dependentPlanActionId: string) {
        const applicableActionFilters = this.dependantPlanActionFilterLookups.filter(actionFilter => {
            if (this.model.planPurpose?.id) {
                // Not all lookups entries declare a list of values in DependantPlanActionFilterLookup.onlyNewPlanPurposeCodes.
                // Those that don't define any values always pass this stage of filtering; an empty cell is a wildcard.
                if (actionFilter.onlyNewPlanPurposeCodes && !actionFilter.onlyNewPlanPurposeCodes.includes(this.model.planPurpose.id)) {
                    return false;
                }
            } else {
                console.debug(`${this.title} controller: No plan purpose defined in the plan lodgement model. Can't filter dependent plan actions.`);
                return false;
            }

            /* Strata dependencies do not have plan purposes in SmartPlan, because purposes are only stored for what
             * SmartPlan sees as a plan - SmartPlan sees plans and strata as mutually exclusive concepts, so it does
             * not store purpose data for strata, only for plans; it has no concept of a Strata plan.
             * This is why this behaviour differs from other analysis sub-components describing dependency action filtering,
             * and why the action filter lookup has a wildcard for dependantPlanPurpose where matchStrata == true
             */

            // Only use action filters applicable to this dependent plan's purpose.
            // A null DependantPlanActionFilterLookup.dependantPlanPurpose cell is a wildcard here.
            if (actionFilter.dependantPlanPurpose && actionFilter.dependantPlanPurpose !== dependentPlan.planPurpose.name) {
                return false;
            }

            // Only use action filters applicable to this dependent plan's plan type.
            if (actionFilter.dependantPlanType !== dependentPlan.planType.name) {
                return false;
            }

            // Only use action filters applicable to this dependent plan's tenure type.
            if (actionFilter.dependantTenureType !== dependentPlan.tenureType.name) {
                return false;
            }

            // The lookup column named like match*, that is applicable to whichever instance of the dependencies controller is checked here.
            return actionFilter[this.matchColumn];
        });

        // The action is valid if its lookup ID matches one or more of the
        // applicable DependantPlanActionFilterLookup.dependantPlanActionId values.
        return applicableActionFilters.some(applicableActionFilter => applicableActionFilter.dependantPlanActionId === dependentPlanActionId);
    }

    dependantPlansActionsAvailable(dependentPlan: DependantPlan) {
        return this.dependantPlanActionLookups.some(actionLookup => this.actionApplicableToDependentPlan(dependentPlan, actionLookup.id));
    }

    /*
     * Clear treatment details input box when changing action from one that requires treatment details to one that does not accept them.
     */

    onActionChange(dependantPlan: DependantPlan, dependencyType: string, filteredIndex: number) {
        const index = this.getDependantIndex(dependencyType, filteredIndex);

        const model =
            this.dependencyType !== 'STRATA'
                ? this.planModelService.getModel().dependantPlans[index]
                : this.planModelService.getModel().strataPlanActions[index];

        if (model && dependantPlan.action) {
            model.action = dependantPlan.action;
        }

        if (!this.expectTreatmentDetails(dependantPlan) && dependantPlan.treatmentDetails) {
            delete dependantPlan.treatmentDetails;
            delete model.treatmentDetails;
        }
    }

    onTreatMentDetailsChange(dependantPlan: DependantPlan, dependencyType: string, filteredIndex: number) {
        const index = this.getDependantIndex(dependencyType, filteredIndex);

        const model =
            this.dependencyType !== 'STRATA'
                ? this.planModelService.getModel().dependantPlans[index]
                : this.planModelService.getModel().strataPlanActions[index];
        if (model && dependantPlan.treatmentDetails) {
            model.treatmentDetails = dependantPlan.treatmentDetails;
        }
    }

    getDependantIndex(dependencyType: string, filteredIdx: number) {
        const source =
            this.dependencyType !== 'STRATA' ? this.planModelService.getModel().dependantPlans : this.planModelService.getModel().strataPlanActions;
        const matches = source.map((item, index) => ({item, index})).filter(({item}) => item.dependencyType === dependencyType);

        return matches[filteredIdx]?.index ?? -1;
    }

    isActionDisabled() {
        return this.planModelService.readonly || this.permissionsService.isDisabledByPermissions(this.editViewAnalysisPermissions);
    }

    isStrataDependency(dependentPlan: DependantPlan | StrataPlanAction) {
        return dependentPlan.dependencyType === 'STRATA';
    }

    getErrorMessageIfNoDependentStrataActions() {
        console.debug(`${this.title} component getErrorMessageIfNoDependentStrataActions`);

        if (this.model.tenureType && this.model.planType && this.model.planPurpose) {
            const formerTenureValidityLookup = this.formerTenureValidityLookups.find(
                v =>
                    v.tenureType === this.model.tenureType.name &&
                    v.planType === this.model.planType.name &&
                    v.planPurpose === this.model.planPurpose.name
            );

            if (formerTenureValidityLookup) {
                return formerTenureValidityLookup.errorMessageIfNoStrataActions;
            }

            console.error(
                `${this.title} controller getStrataActionsUnavailableMessage - No 'errorMessageIfNoStrataAction' available for plan [TenureType : ` +
                    `${this.model.tenureType.name}  PlanType : ${this.model.planType.name}  PlanPurpose : ${this.model.planPurpose.name}]`
            );
        }

        return '';
    }

    /**
     * Determines if the selected action for a dependent plan is valid, based on validity of expected values.
     * No action is considered invalid.
     *
     * @param dependentPlan The dependent plan.
     * @returns {boolean} Whether or not the selected action is valid.
     */
    isActionValid(dependentPlan: DependantPlan | StrataPlanAction) {
        if (
            (this.expectTreatmentDetails(dependentPlan) && !this.isTreatmentDetailsValid(dependentPlan)) ||
            (this.expectConversionNumber(dependentPlan) && !this.hasConversionNumber(dependentPlan))
        ) {
            return false;
        }

        return !!dependentPlan.action;
    }

    expectTreatmentDetails(dependentPlan: DependantPlan | StrataPlanAction) {
        return this.dependantPlanActionLookups.some(
            l =>
                l.id === dependentPlan.action &&
                (l.treatmentDetails === this.OPTIONALITY_VALUES.MANDATORY || l.treatmentDetails === this.OPTIONALITY_VALUES.OPTIONAL)
        );
    }

    treatmentDetailsDisabled() {
        return this.planModelService.readonly || this.permissionsService.isDisabledByPermissions(this.editViewAnalysisPermissions);
    }

    actionValidityTooltip(dependentPlan) {
        if (this.isActionValid(dependentPlan)) {
            return 'The proposed action has been provided for this dependent plan';
        }

        return dependentPlan.action
            ? 'The treatment details of the proposed action have not been provided'
            : 'The proposed action has not been provided for this dependent plan';
    }

    /**
     * Determines if the (strata) dependency requires a conversion number.
     * A survey-strata plan that is being terminated needs to be converted, therefore needs a conversion number.
     *
     * @param dependantPlan The dependent (strata) plan.
     * @returns {boolean}
     */
    expectConversionNumber(dependantPlan: DependantPlan | StrataPlanAction) {
        return this.dependantPlanActionLookups.some(
            l =>
                l.id === dependantPlan.action &&
                (l.conversionPlan === this.OPTIONALITY_VALUES.MANDATORY ||
                    (l.conversionPlan === this.OPTIONALITY_VALUES.LEGACY && this.hasConversionNumber(dependantPlan)))
        );
    }

    hasConversionNumber(dependantPlan: DependantPlan | StrataPlanAction) {
        if (this.isStrataDependency(dependantPlan)) {
            const strataPlanAction: StrataPlanAction = dependantPlan;
            return strataPlanAction.conversionPlanNumber;
        }
        return false;
    }

    conversionNumberDisabled() {
        return this.planModelService.readonly || this.permissionsService.isDisabledByPermissions(this.editViewAnalysisPermissions);
    }

    conversionPlanPlaceholderFromActionId(id: string) {
        return id ? this.actionLookupFromId(id).conversionPlanPlaceholder : '';
    }

    conversionPlanTooltipFromActionId(id) {
        return id ? this.actionLookupFromId(id).conversionPlanTooltip : '';
    }

    /**
     * Derives the match* column name on DependentPlanActionFilterLookup, from the dependency type.
     *
     * @param dependencyType The dependency type (e.g. FORMER_TENURE)
     * @return {String} The match column name (e.g. matchFormerTenure).
     */
    private deriveMatchColumnName(dependencyType: string) {
        const words = dependencyType.split('_');

        let columnName = 'match';
        for (let i = 0; i < words.length; i++) {
            columnName += this.capitalizeFirstLetter(words[i].toLowerCase());
        }
        return columnName;
    }

    private capitalizeFirstLetter(word: string) {
        return word.charAt(0).toUpperCase() + word.slice(1);
    }

    private isTreatmentDetailsValid(dependentPlan: DependantPlan | StrataPlanAction) {
        return this.dependantPlanActionLookups.some(
            l =>
                l.id === dependentPlan.action &&
                (l.treatmentDetails === this.OPTIONALITY_VALUES.OPTIONAL ||
                    (dependentPlan.treatmentDetails && l.treatmentDetails === this.OPTIONALITY_VALUES.MANDATORY))
        );
    }
}
